import { useMemo } from 'react';
import { useAppContext, useAppDispatch } from '@mfe-presentation/contexts';
import { EActionType } from '@mfe-types/context';
import { IConfiguration, IFields, TStep } from '@mfe-types/form';
import { FieldValues } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { composeMPUMediumParams, removeTrackingParams } from '../../helpers/tracking';

const getCurrentStepDefaultValues = (currentStepFields: IFields) => {
    return Object.keys(currentStepFields).reduce((defaultValues: FieldValues, field) => {
        defaultValues[field] = currentStepFields[field].defaultValue;
        return defaultValues;
    }, {});
};

export const getMaxDisplayedStepNumber = (config: IConfiguration) => {
    const stepNumbers = Object.values(config).map((step) => step.displayedStepNumber ?? 0);
    return Math.max(...stepNumbers);
};

export const getStep: (config: IConfiguration, index: number) => TStep = (config, index) => {
    return config[getStepNames(config)[index]];
};

export const getStepNames: (config: IConfiguration) => Array<string> = (config) =>
    Object.keys(config);

export const getFormDefaultValues = (config: IConfiguration): Record<string, unknown> => {
    return Object.keys(config).reduce((accumulator, currentStepKey: string) => {
        return {
            ...accumulator,
            ...getCurrentStepDefaultValues(config[currentStepKey].getFields(accumulator)),
        };
    }, {});
};

export const isIOSWebView = () => {
    const regexp = /(iPhone|iPod|iPad).*AppleWebKit/i;
    return regexp.test(navigator.userAgent);
};

export const isAndroidWebView = () => {
    const regexp = /Android.*AppleWebKit/i;
    return regexp.test(navigator.userAgent);
};

export const getStepInfo = (
    config: IConfiguration,
    targetStepIndex: number,
    maxStepIndex: number,
) => {
    if (targetStepIndex >= 0 && targetStepIndex <= maxStepIndex) {
        return {
            name: getStepNames(config)[targetStepIndex],
            displayedStepNumber: getStep(config, targetStepIndex).displayedStepNumber,
        };
    }
    return {};
};

const useStepUtils = () => {
    const dispatch = useAppDispatch();
    const { config, formValues, currentStep } = useAppContext();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const steps = useMemo(() => getStepNames(config), [config]);

    const setStepAndNavigate = ({
        nextStepIndex,
        hasRelativePath = true,
    }: {
        nextStepIndex: number;
        hasRelativePath?: boolean;
    }) => {
        const nextStepName = steps[nextStepIndex];
        const stepInfo = {
            name: nextStepName,
            index: nextStepIndex,
            displayedStepNumber: config[nextStepName].displayedStepNumber,
            customSubmit: config[nextStepName]?.customSubmit,
        };

        let params = formValues.estimationId
            ? composeMPUMediumParams(window.location.search, nextStepName)
            : window.location.search;

        // do not remove UTM params from the URL of the update page
        if (currentStep.index === 0 && !searchParams.get('token')) {
            params = removeTrackingParams(params);
        }

        dispatch({
            type: EActionType.SetCurrentStep,
            payload: stepInfo,
        });

        dispatch({
            type: EActionType.SetHasSubmittedCurrentStep,
            payload: false,
        });

        /* the action to go back is either called following:
            - the click event on the Navigation back button
            - the click on the browser back button (catch at parent route level)
        Depending on the level where it has been called, we need to specify a relative path prefix 
        because of the nested routing we can have (ex: immoweb). 
        */
        navigate(`${hasRelativePath ? '../' : ''}${nextStepName}${params}`);
    };

    return { setStepAndNavigate };
};

export default useStepUtils;
